import * as React from 'react';
import { CustomColumnMenu, CustomContextCell, SkeletonLoader, } from './DataGridSlots';
import { DataGridFooter, DataGridPagination } from './DataGridFooterSlots';
import { DataGridHeader, } from './DataGridHeaderSlots';
import { DataGridWrapper, StyledDataGrid } from './DataGrid.styles';
import { GridCell, GridNoRowsOverlay, useGridApiRef, } from '@mui/x-data-grid-premium';
import EmptyTableBody from '../EmptyTableBody';
import { Menu, MenuItem } from '@mui/material';
import { LicenseInfo } from '@mui/x-license-pro';
import { MUIX_TABLE_PREFERENCE } from '../../types';
import { useDispatch } from 'react-redux';
import { addDefaultColumnsToScreenSettings, createScreenSettings, getScreenSettingsByScreenNameTableName, resetScreenSettingsStatus, updateScreenSettings, } from '../../actions';
import { useSelectScreenSettings } from '../../selectors';
import { useGlobalization } from '../../contexts';
LicenseInfo.setLicenseKey('defd4ea211dc3b50be959a489c7e4971Tz04ODgyNixFPTE3NDUxNzAxNDcwMDAsUz1wcmVtaXVtLExNPXN1YnNjcmlwdGlvbixLVj0y');
const densityValues = {
    standard: 51,
    compact: 36,
    comfortable: 67,
};
const DataGrid = (props) => {
    const { t } = useGlobalization();
    const { contextCellMenu, conditionalFormatting, columnFormatting, initialState, ...otherProps } = props;
    const [contextMenuVal, setContextMenuVal] = React.useState('');
    const [bodyHeight, setBodyHeight] = React.useState(0);
    const [paginationModel, setPaginationModel] = React.useState({
        pageSize: 100,
        page: 0,
        ...(props.paginationModel ?? initialState?.pagination?.paginationModel),
    });
    const [sortModel, setSortModel] = React.useState(props.sortModel ?? initialState?.sorting?.sortModel ?? []);
    const [conditionalStyles, setConditionalStyles] = React.useState('');
    const [columns, setColumns] = React.useState(props.columns);
    const [updateFromColumns, setUpdateFromColumns] = React.useState(false);
    const [columnsVisibilityModel, setColumnsVisibilityModel] = React.useState({});
    const [anchorMenu, setAnchorMenu] = React.useState(null);
    const menuOpen = Boolean(anchorMenu);
    const apiRef = useGridApiRef();
    const filterRef = React.useMemo(() => React.createRef(), []);
    const dispatch = useDispatch();
    const { screens } = useSelectScreenSettings();
    const isLoading = React.useMemo(() => {
        return (props.loading ||
            (props.columnsToolbar &&
                screens[props.columnsToolbar.screenName]?.[props.columnsToolbar.tableName].status === 'pending'));
    }, [props.loading, props.columnsToolbar, screens]);
    const handleContextMenu = (event, value) => {
        event.preventDefault();
        if (contextCellMenu) {
            setAnchorMenu(event.currentTarget);
            setContextMenuVal(value);
        }
    };
    const handleMenuClose = () => {
        setAnchorMenu(null);
    };
    const rowHeight = React.useMemo(() => {
        return props.density
            ? densityValues[props.density]
            : densityValues.standard;
    }, [props.rowHeight, props.density]);
    React.useEffect(() => {
        const { tableName, screenName, defaultColumns, shouldAlwaysBeVisibleColumns, } = props.columnsToolbar ?? {};
        if (tableName &&
            screenName &&
            defaultColumns &&
            (!screens[screenName]?.[tableName]?.status ||
                screens[screenName]?.[tableName]?.status === 'idle')) {
            const defaultColumnsClone = [...defaultColumns];
            if (shouldAlwaysBeVisibleColumns &&
                shouldAlwaysBeVisibleColumns.length > 0) {
                let lastOrderNumber = defaultColumnsClone[defaultColumnsClone.length - 1]?.order;
                if (!isNaN(lastOrderNumber)) {
                    shouldAlwaysBeVisibleColumns.forEach(col => {
                        defaultColumnsClone.push({
                            name: col,
                            order: ++lastOrderNumber,
                        });
                    });
                }
            }
            dispatch(addDefaultColumnsToScreenSettings({
                tableName,
                screenName,
                defaultColumns: defaultColumnsClone,
            }));
            if (tableName !== MUIX_TABLE_PREFERENCE.STORY_BOOK_TEST) {
                dispatch(getScreenSettingsByScreenNameTableName({
                    tableName,
                    screenName,
                    defaultColumns: defaultColumnsClone,
                }));
            }
        }
    }, [props.columnsToolbar]);
    React.useEffect(() => {
        if (props.columnsToolbar) {
            const { tableName, screenName } = props.columnsToolbar;
            const columnsSettings = [
                ...(screens[screenName]?.[tableName].configs.columns ?? []),
            ];
            columnsSettings.sort((col1, col2) => col1.order - col2.order);
            const newColumns = [];
            const newVisibilityModel = {};
            const columnsClone = [...columns];
            columnsSettings.forEach(setting => {
                const columnInd = columnsClone.findIndex(col => col.field === setting.name);
                if (columnInd > -1) {
                    newColumns.push(columnsClone[columnInd]);
                    columnsClone.splice(columnInd, 1);
                }
            });
            columnsClone.forEach(col => {
                newColumns.push(col);
                newVisibilityModel[col.field] = false;
            });
            setColumns(newColumns);
            setColumnsVisibilityModel(newVisibilityModel);
        }
        else {
            setColumns(props.columns);
        }
    }, [screens, props.columns]);
    React.useEffect(() => {
        if (!props.paginationModel)
            return;
        setPaginationModel(props.paginationModel);
    }, [props.paginationModel]);
    React.useEffect(() => {
        if (!props.sortModel)
            return;
        setSortModel(props.sortModel);
    }, [props.sortModel]);
    React.useEffect(() => {
        if (props.autoRowSizing) {
            const itemsPerPage = Math.ceil((bodyHeight - rowHeight) / rowHeight);
            setPaginationModel(prev => ({
                ...prev,
                pageSize: itemsPerPage >= 0 ? itemsPerPage : 0,
            }));
        }
    }, [bodyHeight, props.rowHeight]);
    React.useEffect(() => {
        const cellsFormatting = Object.keys(conditionalFormatting ?? {}).reduce((sx, row) => {
            const columns = Object.keys(conditionalFormatting?.[row] ?? {});
            columns.forEach(col => {
                const styles = conditionalFormatting?.[row][col].styles;
                styles?.forEach(style => {
                    sx = `${createStyleClass(style)}
						${sx}`;
                });
            });
            return sx;
        }, '');
        const columnsFormatting = Object.keys(columnFormatting ?? {}).reduce((sx, column) => {
            const styles = columnFormatting?.[column].styles;
            styles?.forEach(style => {
                sx = `${createStyleClass(style)}
					${sx}`;
            });
            return sx;
        }, '');
        setConditionalStyles(columnsFormatting + cellsFormatting);
    }, [conditionalFormatting, columnFormatting]);
    React.useEffect(() => {
        if (updateFromColumns)
            updateColumnsOrder();
    }, [updateFromColumns]);
    React.useEffect(() => {
        return () => {
            if (props.columnsToolbar) {
                const { tableName, screenName } = props.columnsToolbar;
                dispatch(resetScreenSettingsStatus({ tableName, screenName }));
            }
        };
    }, []);
    const getCellClassName = React.useCallback((params) => {
        let selectedColumn = '';
        if (props.selectedColumn && props.selectedColumn === params.field) {
            selectedColumn = 'selected-column ';
            //@ts-ignore
            const gridApi = params.api;
            const rowIndex = gridApi.getRowIndexRelativeToVisibleRows(params.row.id);
            const totalPageRows = gridApi.state.rowsMeta.positions.length - 1;
            if (rowIndex === 0)
                selectedColumn += 'selected-column-top ';
            if (rowIndex === totalPageRows)
                selectedColumn += 'selected-column-bottom ';
        }
        if (!conditionalFormatting && !columnFormatting)
            return selectedColumn;
        let resultClass = '';
        const checkForRule = (rule) => {
            if (rule.withoutRule) {
                resultClass = rule.className;
            }
            else {
                if (eval(`${params.value} ${rule.rule}`)) {
                    resultClass = rule.className;
                }
            }
        };
        columnFormatting?.[params.field]?.rules?.forEach(checkForRule);
        conditionalFormatting?.[params.id]?.[params.field]?.rules?.forEach(checkForRule);
        return selectedColumn + resultClass;
    }, [conditionalFormatting, columnFormatting, props.selectedColumn]);
    const getTogglableColumns = (columns) => {
        // Hide the checkbox column by default
        return columns
            .filter(column => column.field !== '__check__' &&
            !props.nonTogglableColumns?.includes(column.field))
            .map(column => column.field);
    };
    const textDecoration = (style) => {
        if (!style.lineThrough && !style.underline)
            return 'none';
        return `${style.lineThrough ? 'line-through' : ''} ${style.underline ? 'underline' : ''}`;
    };
    const createStyleClass = (style) => {
        const keyClass = `& .${style.className}`;
        return `${keyClass} {
		background-color: ${style.bgColor} !important;
		color: ${style.color} !important;
		font-weight: ${style.bold ? 'bold' : 'normal'} !important;
		font-style: ${style.italic ? 'italic' : 'normal'} !important;
		text-decoration: ${textDecoration(style)} !important;
	}`;
    };
    const updateColumnsOrder = () => {
        if (props.columnsToolbar &&
            apiRef.current &&
            props.columnsToolbar.tableName !==
                MUIX_TABLE_PREFERENCE.STORY_BOOK_TEST) {
            const { tableName, screenName, defaultColumns } = props.columnsToolbar;
            const visibleColumns = apiRef.current.getVisibleColumns();
            const columnsBody = visibleColumns.map((col, index) => ({
                name: col.field,
                order: index,
            }));
            const requestBody = {
                tableConfigs: {
                    tableName,
                    screenName,
                    columns: columnsBody,
                },
            };
            const { screenSettingsId } = screens[screenName]?.[tableName];
            if (screenSettingsId) {
                dispatch(updateScreenSettings({
                    ...requestBody,
                    screenSettingsId,
                    defaultColumns: defaultColumns ?? [],
                }));
            }
            else {
                dispatch(createScreenSettings({
                    ...requestBody,
                    defaultColumns: defaultColumns ?? [],
                }));
            }
            setUpdateFromColumns(false);
        }
    };
    const filterSlotProps = () => {
        if (props.useFilterRef) {
            return {
                panel: {
                    anchorEl: filterRef.current,
                },
            };
        }
        else {
            return {};
        }
    };
    return (React.createElement(React.Fragment, null,
        contextCellMenu && (React.createElement(Menu, { anchorEl: anchorMenu, open: menuOpen, onClose: handleMenuClose, anchorOrigin: contextCellMenu.anchor }, contextCellMenu.options.map(option => (React.createElement(MenuItem, { key: option.key, onClick: e => {
                option.onClick(e, contextMenuVal);
                handleMenuClose();
            } }, option.key))))),
        React.createElement(DataGridWrapper, { "$height": props.height },
            React.createElement(StyledDataGrid, { ...otherProps, initialState: initialState, apiRef: props.apiRef ?? apiRef, columnVisibilityModel: !props.columnsToolbar
                    ? props.columnVisibilityModel
                    : columnsVisibilityModel, onColumnVisibilityModelChange: (newModel, details) => {
                    setColumnsVisibilityModel(newModel);
                    setUpdateFromColumns(true);
                    props.onColumnVisibilityModelChange?.(newModel, details);
                }, onColumnOrderChange: (params, options, callbackDetails) => {
                    setUpdateFromColumns(true);
                    if (props.onColumnOrderChange) {
                        props.onColumnOrderChange?.(params, options, callbackDetails);
                    }
                }, loading: isLoading, autoHeight: props.autoHeight ?? true, rows: isLoading ? [] : props.rows, columns: columns, 
                //  Here you can create custom slots for your data grid
                slots: {
                    toolbar: DataGridHeader,
                    footer: DataGridFooter,
                    pagination: DataGridPagination,
                    loadingOverlay: SkeletonLoader,
                    columnMenu: CustomColumnMenu,
                    cell: contextCellMenu ? CustomContextCell : GridCell,
                    noRowsOverlay: props.emptyState
                        ? EmptyTableBody
                        : GridNoRowsOverlay,
                    ...props.slots,
                }, 
                //If you need to pass a prop to your custom slots here is where
                slotProps: {
                    toolbar: {
                        title: props.title,
                        search: props.search,
                        columnsToolbar: props.columnsToolbar,
                        filterToolbar: props.filterToolbar,
                        exportButton: props.exportButton,
                        refreshButton: props.refreshButton,
                        actionsMenu: props.actionsMenu,
                        toolbarContentJustifyDirection: props.toolbarContentJustifyDirection,
                        toolbarContainerJustifyDirection: props.toolbarContainerJustifyDirection,
                        extraContent: props.extraToolbarContent,
                        extraButtons: props.extraButtons,
                        exportProps: props.export,
                        loading: isLoading,
                        weave: props.weave,
                        filterButton: { filterRef: filterRef },
                    },
                    footer: {
                        autoRowSizing: props.autoRowSizing,
                        pageSizeOptions: props.pageSizeOptions,
                        pagination: props.pagination,
                        footerHelper: props.footerHelper,
                        rowCount: props.rowCount,
                    },
                    loadingOverlay: {
                        skeletonRowCount: props.skeletonRowCount,
                    },
                    row: {
                        style: {
                            cursor: contextCellMenu
                                ? 'context-menu'
                                : 'default',
                        },
                    },
                    cell: {
                        onContextMenu: contextCellMenu
                            ? handleContextMenu
                            : undefined,
                    },
                    columnMenu: props.customColumnMenu,
                    columnsPanel: {
                        getTogglableColumns,
                    },
                    noRowsOverlay: props.emptyState || {},
                    ...props.slotProps,
                    ...filterSlotProps(),
                }, conditionalStyles: conditionalStyles ?? '', getCellClassName: getCellClassName, onResize: (elementSize) => setBodyHeight(elementSize.height -
                    densityValues[props.density ?? 'standard']), sortModel: sortModel, onSortModelChange: (model, details) => {
                    setSortModel(model);
                    props.onSortModelChange?.(model, details);
                }, paginationModel: paginationModel, onPaginationModelChange: (model, details) => {
                    setPaginationModel(model);
                    props.onPaginationModelChange?.(model, details);
                } }))));
};
export default DataGrid;
